/**
 * Sound effects using Web Audio API — pure synthesis, no audio files.
 *
 * - playRewardSound()      — coin-drop clink when a reward arrives
 * - playSeatClickSound()   — single seat click (mechanical ka-chunk)
 * - playBulkSelectSound()  — bulk seat selection (mechanical ratchet tick)
 *
 * Two independent mute toggles are persisted in localStorage:
 *   - SFX   (`takeover-sound-muted`)  — all synthesized + sampled FX. Defaults OFF (on).
 *   - Music (`takeover-music-muted`)  — the ambient background score. Defaults ON (off).
 *
 * The functions exported as `isSoundMuted` / `setSoundMuted` / `toggleSoundMuted`
 * continue to gate SFX only so existing call sites (every `if (isSoundMuted()) return;`
 * guard across the codebase) stay correct. Music gets its own matching trio.
 */

import { createSoundBufferManager } from "./sound-buffer";

const MUTE_KEY = 'takeover-sound-muted';
const MUSIC_MUTE_KEY = 'takeover-music-muted';

let audioCtx: AudioContext | null = null;

// ─── Grid sound priority gate ────────────────────────────────────────────────
// When multiple grid events fire in a batch (e.g. SSE update with buyout +
// forfeit + claim), only the highest-priority sound plays. Lower-priority
// sounds within the cooldown window are silently dropped.

type GridSoundType = 'buyout' | 'claim' | 'abandon' | 'forfeit';

const GRID_SOUND_PRIORITY: Record<GridSoundType, number> = {
  buyout: 4,
  claim: 3,
  abandon: 2,
  forfeit: 1,
};

const GRID_SOUND_COOLDOWN_MS = 1500;
let lastGridSoundTime = 0;
let lastGridSoundPriority = 0;

/**
 * Returns true if this sound should play, false if a higher-priority
 * grid sound already played within the cooldown window.
 */
function shouldPlayGridSound(type: GridSoundType): boolean {
  const now = Date.now();
  const priority = GRID_SOUND_PRIORITY[type];

  if (now - lastGridSoundTime > GRID_SOUND_COOLDOWN_MS) {
    lastGridSoundTime = now;
    lastGridSoundPriority = priority;
    return true;
  }

  if (priority > lastGridSoundPriority) {
    lastGridSoundTime = now;
    lastGridSoundPriority = priority;
    return true;
  }

  return false;
}

/** Check if SFX are currently muted. */
export function isSoundMuted(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(MUTE_KEY) === '1';
}

/** Set SFX muted state and persist. Dispatches a custom event so all UI toggles stay in sync. */
export function setSoundMuted(muted: boolean) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  window.dispatchEvent(new CustomEvent('sound-mute-changed', { detail: { muted } }));
}

/** Toggle SFX muted state. Returns the new value. */
export function toggleSoundMuted(): boolean {
  const next = !isSoundMuted();
  setSoundMuted(next);
  return next;
}

// ─── Music (ambient background score) ───────────────────────────────────────
// Music defaults to OFF (muted). Storage values:
//   absent → muted  (new users / never-touched-toggle)
//   '0'    → unmuted (user has explicitly turned music ON)
//   '1'    → muted   (user has explicitly turned music OFF)
// Flipping the absent-key default from "unmuted" to "muted" preserves the
// only signal we actually have from existing users — anyone who has ever
// hit the music pill writes `'0'` or `'1'` and so keeps their stated
// preference across this change.

/** Check if music is currently muted. Defaults to `true` (silent until user opts in). */
export function isMusicMuted(): boolean {
  if (typeof window === 'undefined') return true;
  return localStorage.getItem(MUSIC_MUTE_KEY) !== '0';
}

/** Set music muted state and persist. Dispatches `music-mute-changed`. */
export function setMusicMuted(muted: boolean) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(MUSIC_MUTE_KEY, muted ? '1' : '0');
  window.dispatchEvent(new CustomEvent('music-mute-changed', { detail: { muted } }));
}

/** Toggle music muted state. Returns the new value. */
export function toggleMusicMuted(): boolean {
  const next = !isMusicMuted();
  setMusicMuted(next);
  return next;
}

/**
 * Bridge cross-tab music mute changes into the same-window CustomEvent
 * pipeline that `setMusicMuted` already feeds.
 *
 * Background: `setMusicMuted()` writes to localStorage and dispatches
 * `music-mute-changed` on `window`. Both signals are local to the
 * originating tab — `window.dispatchEvent` never crosses windows, and
 * the `storage` event (which *would* propagate) wasn't being listened
 * to anywhere. The result on a multi-tab session was:
 *
 *   • the music player runs as a single-tab leader via `navigator.locks`,
 *     so only the leader tab owns an `<audio>` element;
 *   • toggling the music pill in a follower tab flipped localStorage
 *     and the follower's UI but never reached the leader, so audio
 *     kept playing while the user-visible state said "muted" (and
 *     vice versa). The user reports this as "toggling music on/off
 *     doesn't appear to work."
 *
 * Fix: subscribe to `storage` once, filter for the music mute key,
 * and re-dispatch the existing `music-mute-changed` CustomEvent
 * locally with a freshly-read `isMusicMuted()` value. Per the storage
 * event spec, the originating tab does *not* receive its own write,
 * so the originator keeps using the synchronous CustomEvent path
 * `setMusicMuted` already dispatches — no double handling, no
 * feedback loop. Every other tab's existing listeners
 * (`MusicController.onMuteChange` to fade leader audio,
 * `LobbyFooter.onMusic` to refresh the pill) fire identically to the
 * originating-tab path.
 *
 * Returns a teardown function so the caller (currently the headless
 * `MusicController` mounted once at the root layout) can unwire on
 * unmount. SSR-safe: returns a no-op when `window` is undefined.
 */
export function startMusicMuteCrossTabSync(): () => void {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (e: StorageEvent) => {
    // `e.key === null` happens on `localStorage.clear()` from another
    // tab — the music key is implicitly absent (= unmuted) afterwards,
    // so re-broadcasting still produces the right state. Otherwise we
    // only care about our own key to avoid spamming listeners on
    // every unrelated localStorage write.
    if (e.key !== null && e.key !== MUSIC_MUTE_KEY) return;
    window.dispatchEvent(
      new CustomEvent('music-mute-changed', {
        detail: { muted: isMusicMuted() },
      }),
    );
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

/**
 * Warm up the AudioContext from a user gesture (click/tap).
 * Must be called from an event handler to unlock audio on mobile/Chrome.
 * Subsequent calls to play* functions will then work from any context.
 */
export function ensureAudioReady() {
  try {
    getAudioContext();
  } catch {
    // non-critical
  }
}

// ─── Mechanical click sounds ─────────────────────────────────────────────────

/**
 * Single seat click — a heavy mechanical "ka-chunk", like a relay switch
 * or an old typewriter key bottoming out. Two-part: a sharp noise transient
 * (the impact) followed by a low resonant thud (the body).
 */
export function playSeatClickSound() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.35, now);
    master.connect(ctx.destination);

    // Part 1: sharp noise crack — the mechanical impact
    const crackLen = Math.round(ctx.sampleRate * 0.008);
    const crackBuf = ctx.createBuffer(1, crackLen, ctx.sampleRate);
    const crackData = crackBuf.getChannelData(0);
    for (let i = 0; i < crackLen; i++) {
      crackData[i] = (Math.random() * 2 - 1);
    }
    const crack = ctx.createBufferSource();
    crack.buffer = crackBuf;
    const crackHpf = ctx.createBiquadFilter();
    crackHpf.type = 'highpass';
    crackHpf.frequency.setValueAtTime(2000, now);
    const crackGain = ctx.createGain();
    crackGain.gain.setValueAtTime(1.0, now);
    crackGain.gain.exponentialRampToValueAtTime(0.001, now + 0.012);
    crack.connect(crackHpf).connect(crackGain).connect(master);
    crack.start(now);
    crack.stop(now + 0.015);

    // Part 2: low resonant thud — the body/weight of the mechanism
    const thud = ctx.createOscillator();
    thud.type = 'sine';
    thud.frequency.setValueAtTime(120, now);
    thud.frequency.exponentialRampToValueAtTime(60, now + 0.04);
    const thudGain = ctx.createGain();
    thudGain.gain.setValueAtTime(0.6, now);
    thudGain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
    thud.connect(thudGain).connect(master);
    thud.start(now);
    thud.stop(now + 0.06);

    // Part 3: brief metallic ring — spring/latch resonance
    const ring = ctx.createOscillator();
    ring.type = 'square';
    ring.frequency.setValueAtTime(3200, now);
    const ringGain = ctx.createGain();
    ringGain.gain.setValueAtTime(0.08, now);
    ringGain.gain.exponentialRampToValueAtTime(0.001, now + 0.025);
    ring.connect(ringGain).connect(master);
    ring.start(now);
    ring.stop(now + 0.03);
  } catch {
    // non-critical
  }
}

/**
 * Bulk selection click — a lighter, snappier mechanical tick. Think of a
 * ratchet advancing one notch, or a DIP switch being flipped. Tighter and
 * faster than the single click so rapid-fire bulk selection feels crisp.
 */
export function playBulkSelectSound() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.35, now);
    master.connect(ctx.destination);

    // Part 1: sharp tick — filtered noise snap
    const tickLen = Math.round(ctx.sampleRate * 0.003);
    const tickBuf = ctx.createBuffer(1, tickLen, ctx.sampleRate);
    const tickData = tickBuf.getChannelData(0);
    for (let i = 0; i < tickLen; i++) {
      tickData[i] = (Math.random() * 2 - 1);
    }
    const tick = ctx.createBufferSource();
    tick.buffer = tickBuf;
    const tickBpf = ctx.createBiquadFilter();
    tickBpf.type = 'bandpass';
    tickBpf.frequency.setValueAtTime(3500 + Math.random() * 1500, now);
    tickBpf.Q.setValueAtTime(1.5, now);
    const tickGain = ctx.createGain();
    tickGain.gain.setValueAtTime(1.0, now);
    tickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.006);
    tick.connect(tickBpf).connect(tickGain).connect(master);
    tick.start(now);
    tick.stop(now + 0.008);

    // Part 2: tiny ping — the latch catching
    const ping = ctx.createOscillator();
    ping.type = 'square';
    ping.frequency.setValueAtTime(1800 + Math.random() * 400, now);
    const pingGain = ctx.createGain();
    pingGain.gain.setValueAtTime(0.18, now);
    pingGain.gain.exponentialRampToValueAtTime(0.001, now + 0.008);
    ping.connect(pingGain).connect(master);
    ping.start(now);
    ping.stop(now + 0.01);
  } catch {
    // non-critical
  }
}

// ─── Mint sound — metallic clink with gold shimmer ───────────────────────────

/**
 * Metallic "clink" for minting (pre-graduation claims). Two layered coin
 * strikes with a bright shimmer tail — distinct from the post-graduation
 * claim whoosh. Total: ~350ms.
 */
export function playMintSound(durationMs: number = 350) {
  if (isSoundMuted()) return;
  if (!shouldPlayGridSound('claim')) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.3, now);
    master.connect(ctx.destination);

    // Strike 1: bright metallic hit — E7 (2637 Hz)
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(2637, now);
    osc1.frequency.exponentialRampToValueAtTime(2400, now + 0.06);
    const g1 = ctx.createGain();
    g1.gain.setValueAtTime(0.001, now);
    g1.gain.linearRampToValueAtTime(0.6, now + 0.005);
    g1.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
    osc1.connect(g1).connect(master);
    osc1.start(now);
    osc1.stop(now + 0.12);

    // Inharmonic partial for metallic timbre
    const osc1b = ctx.createOscillator();
    osc1b.type = 'triangle';
    osc1b.frequency.setValueAtTime(2637 * 2.76, now);
    const g1b = ctx.createGain();
    g1b.gain.setValueAtTime(0.2, now + 0.002);
    g1b.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
    osc1b.connect(g1b).connect(master);
    osc1b.start(now);
    osc1b.stop(now + 0.08);

    // Noise transient — impact click
    const noiseLen = Math.round(ctx.sampleRate * 0.008);
    const noiseBuf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
    const noiseData = noiseBuf.getChannelData(0);
    for (let i = 0; i < noiseLen; i++) {
      noiseData[i] = (Math.random() * 2 - 1) * 0.5;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    const noiseBpf = ctx.createBiquadFilter();
    noiseBpf.type = 'bandpass';
    noiseBpf.frequency.setValueAtTime(5000, now);
    noiseBpf.Q.setValueAtTime(1.5, now);
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.5, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.01);
    noise.connect(noiseBpf).connect(noiseGain).connect(master);
    noise.start(now);
    noise.stop(now + 0.015);

    // Strike 2: softer bounce at higher pitch — A7 (3520 Hz), delayed
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(3520, now + 0.07);
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.001, now);
    g2.gain.linearRampToValueAtTime(0.3, now + 0.075);
    g2.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
    osc2.connect(g2).connect(master);
    osc2.start(now + 0.07);
    osc2.stop(now + 0.2);

    // Gold shimmer tail — detuned high sine pair
    for (const detune of [-6, 6]) {
      const shim = ctx.createOscillator();
      shim.type = 'sine';
      shim.frequency.setValueAtTime(4186, now + 0.1);
      shim.detune.setValueAtTime(detune, now + 0.1);
      const shimGain = ctx.createGain();
      shimGain.gain.setValueAtTime(0.001, now + 0.1);
      shimGain.gain.linearRampToValueAtTime(0.08, now + 0.12);
      shimGain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
      shim.connect(shimGain).connect(master);
      shim.start(now + 0.1);
      shim.stop(now + 0.35);
    }
  } catch {
    // non-critical
  }
}

// ─── Claim sound — MP3 whoosh ────────────────────────────────────────────────

const claimSoundBuffer = createSoundBufferManager({
  getAudioContext,
  path: "/sounds/claim.mp3",
});
claimSoundBuffer.preload();

/**
 * Whooshing air — plays the claim MP3 sped up to fit the claim animation.
 * The original file is ~1.0s, playbackRate adjusts to the target duration.
 *
 * @param durationMs  Target duration to fit the sound into (default 600ms).
 */
export function playClaimSound(durationMs: number = 600) {
  if (isSoundMuted()) return;
  if (!shouldPlayGridSound('claim')) return;
  try {
    const ctx = getAudioContext();
    const buf = claimSoundBuffer.getBuffer();
    if (!buf) {
      void claimSoundBuffer.loadBuffer();
      return;
    }

    const now = ctx.currentTime;
    const targetSec = durationMs / 1000;
    const rate = buf.duration / targetSec;

    const source = ctx.createBufferSource();
    source.buffer = buf;
    source.playbackRate.setValueAtTime(rate, now);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.4, now);
    gain.gain.setValueAtTime(0.4, now + targetSec * 0.8);
    gain.gain.exponentialRampToValueAtTime(0.001, now + targetSec);

    source.connect(gain).connect(ctx.destination);
    source.start(now);
    source.stop(now + targetSec + 0.05);
  } catch {
    // non-critical
  }
}

/** Single bling note: clean sine with a quick attack and smooth ring-out. */
function blingNote(
  ctx: AudioContext,
  destination: AudioNode,
  frequency: number,
  startTime: number,
  attack: number,
  decay: number,
  peak: number,
) {
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(frequency, startTime);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.001, startTime);
  gain.gain.linearRampToValueAtTime(peak, startTime + attack);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + attack + decay);

  osc.connect(gain).connect(destination);
  osc.start(startTime);
  osc.stop(startTime + attack + decay + 0.02);
}

// ─── Buyout sound — clean click/bling ────────────────────────────────────────

/**
 * Clean click at the end of the takeover animation.
 * Just the bling notes — a quick ascending chime that plays when the
 * animation is completing. No rumble/vibration lead-in.
 *
 * @param durationMs  Total animation duration (default 1600ms).
 *                    The click plays at the tail end (~last 25%).
 */
export function playBuyoutSound(durationMs: number = 1600) {
  if (isSoundMuted()) return;
  if (!shouldPlayGridSound('buyout')) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const d = durationMs / 1000;
    const blingDur = d * 0.25;
    const blingStart = now + d * 0.75;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.3, now);
    master.connect(ctx.destination);

    const blingDecay = blingDur * 0.6;
    blingNote(ctx, master, 1318.5, blingStart, 0.01, blingDecay, 0.6);
    blingNote(ctx, master, 1975.5, blingStart + blingDur * 0.2, 0.01, blingDecay, 0.5);
    blingNote(ctx, master, 2637,   blingStart + blingDur * 0.25, 0.01, blingDecay * 1.2, 0.15);
  } catch {
    // non-critical
  }
}

// ─── Warm fanfare — fee alert / takeover ─────────────────────────────────────

/**
 * Soft, rewarding fanfare — a warm ascending major triad (C4 → E4 → G4)
 * using triangle waves with gentle attacks and long, smooth decays.
 * Feels like a "you earned this" moment, not a jump scare.
 * Total duration: ~600ms.
 */
export function playFeeAlertSound() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.15, now);
    master.connect(ctx.destination);

    // C4 → E4 → G4  (warm register, gentler than C5)
    warmNote(ctx, master, 261.63, now,        0.12, 0.18, 0.35); // C4 — soft lead-in
    warmNote(ctx, master, 329.63, now + 0.14, 0.10, 0.20, 0.45); // E4 — builds
    warmNote(ctx, master, 392.00, now + 0.28, 0.10, 0.30, 0.50); // G4 — blooms and lingers
  } catch {
    // non-critical
  }
}

/**
 * Heavenly "ahhh" — a slow major-chord swell for the full-board moment
 * (every tile selected). Layers `warmNote`s across two octaves with long,
 * gentle attacks and a high shimmer on top so it blooms like a choir
 * rather than firing as a UI ping. Reuses the warm triangle+chorus voice;
 * the long decays are what give it the celestial sustain.
 */
export function playFullBoardChord() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.16, now);
    master.connect(ctx.destination);

    // C major: root → 3rd → 5th → octave, staggered like a swelling choir,
    // capped with a high E5 shimmer for the "ahh" lift.
    warmNote(ctx, master, 261.63, now,        0.30, 1.3, 0.30); // C4
    warmNote(ctx, master, 329.63, now + 0.12, 0.30, 1.3, 0.30); // E4
    warmNote(ctx, master, 392.0,  now + 0.24, 0.32, 1.4, 0.32); // G4
    warmNote(ctx, master, 523.25, now + 0.40, 0.34, 1.5, 0.30); // C5 — the lift
    warmNote(ctx, master, 659.25, now + 0.58, 0.36, 1.6, 0.18); // E5 — shimmer
  } catch {
    // non-critical
  }
}

/**
 * Single warm note: triangle wave (gentle harmonics) with a soft swell
 * and a smooth fade. A detuned sine layer adds subtle chorus warmth.
 */
function warmNote(
  ctx: AudioContext,
  destination: AudioNode,
  frequency: number,
  startTime: number,
  attack: number,
  decay: number,
  peak: number,
) {
  // Primary tone — triangle wave for a soft, rounded timbre
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(frequency, startTime);

  // Gentle lowpass — keeps it mellow, no harshness
  const lpf = ctx.createBiquadFilter();
  lpf.type = 'lowpass';
  lpf.frequency.setValueAtTime(frequency * 3, startTime);
  lpf.frequency.linearRampToValueAtTime(frequency * 4, startTime + attack);
  lpf.frequency.exponentialRampToValueAtTime(frequency * 2, startTime + attack + decay);
  lpf.Q.setValueAtTime(0.7, startTime);

  // Smooth amplitude envelope — gradual swell, gentle fade
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.001, startTime);
  gain.gain.linearRampToValueAtTime(peak, startTime + attack);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + attack + decay);

  osc.connect(lpf).connect(gain).connect(destination);
  osc.start(startTime);
  osc.stop(startTime + attack + decay + 0.02);

  // Chorus layer — slightly detuned sine for warmth
  const osc2 = ctx.createOscillator();
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(frequency * 1.004, startTime);

  const gain2 = ctx.createGain();
  gain2.gain.setValueAtTime(0.001, startTime);
  gain2.gain.linearRampToValueAtTime(peak * 0.4, startTime + attack);
  gain2.gain.exponentialRampToValueAtTime(0.001, startTime + attack + decay);

  osc2.connect(lpf);
  osc2.connect(gain2).connect(destination);
  osc2.start(startTime);
  osc2.stop(startTime + attack + decay + 0.02);
}

// ─── Tutorial squeaky talk sound ─────────────────────────────────────────────

/**
 * Squeaky character talk — short random chirp for each typed character.
 * Think: Animal Crossing villager speech / Banjo-Kazooie mumble.
 * A very short (30-60ms) pitched square wave with a noise burst.
 * Each call is slightly randomised for a natural "babbling" feel.
 */
export function playTutorialTalkSound() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.12, now);
    master.connect(ctx.destination);

    // Randomise pitch in a squeaky range (400-900 Hz)
    const freq = 400 + Math.random() * 500;
    const duration = 0.03 + Math.random() * 0.03; // 30-60ms

    // Square wave chirp — the core "voice"
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(freq, now);
    // Slight pitch slide for expressiveness
    osc.frequency.exponentialRampToValueAtTime(freq * (0.85 + Math.random() * 0.3), now + duration);

    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(0.6, now);
    oscGain.gain.exponentialRampToValueAtTime(0.001, now + duration);

    // Bandpass to soften the harsh square wave
    const bpf = ctx.createBiquadFilter();
    bpf.type = 'bandpass';
    bpf.frequency.setValueAtTime(freq * 1.5, now);
    bpf.Q.setValueAtTime(1.2, now);

    osc.connect(bpf).connect(oscGain).connect(master);
    osc.start(now);
    osc.stop(now + duration + 0.01);
  } catch {
    // non-critical
  }
}

// ─── Tutorial polished SFX (used by the /tutorial overlay) ──────────────────
// The legacy `playTutorialTalkSound` above is a squarewave chirp meant for
// the cartoony "Takeover Man" mascot in the pre-graduation onboarding
// flow. The /tutorial route gates these against the polished app feel —
// short filtered transients with a subtle stereo pan that suggests the
// caret moving left-to-right rather than a chirpy mascot voice. We
// keep the old function around so the pre-grad overlay continues to
// sound like itself.

let tutorialTypeTick = 0;

/**
 * Soft typewriter tick — a high-passed click with a tiny pitched ping
 * tail. Pans ±0.35 across a 16-tick cycle so a paragraph of text reads
 * as the caret travelling across the screen rather than every glyph
 * landing dead-centre. ~6 ms total, low (-15dB) volume so a 3-line
 * paragraph doesn't fatigue.
 */
export function playTutorialTypeSound() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const idx = tutorialTypeTick++;

    // Stereo panner: cycle from -0.35 → +0.35 → -0.35 over 16 ticks.
    // Old WebKit / safe fallback: we feed straight into destination if
    // StereoPannerNode isn't available.
    const panValue = Math.sin((idx * Math.PI) / 8) * 0.35;
    const panner =
      typeof ctx.createStereoPanner === 'function'
        ? ctx.createStereoPanner()
        : null;
    if (panner) panner.pan.setValueAtTime(panValue, now);

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.18, now);
    if (panner) {
      master.connect(panner).connect(ctx.destination);
    } else {
      master.connect(ctx.destination);
    }

    // Part 1: filtered-noise transient — the "click" of a key landing.
    const tickLen = Math.round(ctx.sampleRate * 0.005);
    const tickBuf = ctx.createBuffer(1, tickLen, ctx.sampleRate);
    const tickData = tickBuf.getChannelData(0);
    for (let i = 0; i < tickLen; i++) {
      tickData[i] = Math.random() * 2 - 1;
    }
    const tick = ctx.createBufferSource();
    tick.buffer = tickBuf;
    const tickBpf = ctx.createBiquadFilter();
    tickBpf.type = 'bandpass';
    // Slight per-tick pitch jitter so consecutive characters don't
    // sound like a metronome.
    tickBpf.frequency.setValueAtTime(2200 + Math.random() * 800, now);
    tickBpf.Q.setValueAtTime(1.6, now);
    const tickGain = ctx.createGain();
    tickGain.gain.setValueAtTime(0.85, now);
    tickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.006);
    tick.connect(tickBpf).connect(tickGain).connect(master);
    tick.start(now);
    tick.stop(now + 0.008);

    // Part 2: tiny pitched ping — the "ink-meets-paper" flick.
    const ping = ctx.createOscillator();
    ping.type = 'triangle';
    ping.frequency.setValueAtTime(1900 + Math.random() * 200, now);
    const pingGain = ctx.createGain();
    pingGain.gain.setValueAtTime(0.06, now);
    pingGain.gain.exponentialRampToValueAtTime(0.001, now + 0.012);
    ping.connect(pingGain).connect(master);
    ping.start(now);
    ping.stop(now + 0.014);
  } catch {
    // non-critical
  }
}

/**
 * UI confirmation — "Next" / branch / tutorial advance click. A short
 * sine "ting" with a soft sub-layer below it; reads like a polished
 * console UI confirmation rather than the mechanical seat-click
 * (`playSeatClickSound`) that we use for board interactions. ~140 ms
 * total, centred (no pan), peak around -10 dB.
 */
export function playTutorialAdvanceSound() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.28, now);
    master.connect(ctx.destination);

    // Bright top: sine ting that decays quickly. The exponential ramp
    // gives it a "bell" feel rather than the abrupt cut of a linear
    // envelope.
    const top = ctx.createOscillator();
    top.type = 'sine';
    top.frequency.setValueAtTime(1280, now);
    top.frequency.exponentialRampToValueAtTime(940, now + 0.13);
    const topGain = ctx.createGain();
    topGain.gain.setValueAtTime(0.0, now);
    topGain.gain.linearRampToValueAtTime(0.7, now + 0.005);
    topGain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);
    top.connect(topGain).connect(master);
    top.start(now);
    top.stop(now + 0.15);

    // Sub-layer: half-octave below to give it body. Triangle wave is
    // slightly richer than sine without competing with the top.
    const sub = ctx.createOscillator();
    sub.type = 'triangle';
    sub.frequency.setValueAtTime(640, now);
    sub.frequency.exponentialRampToValueAtTime(480, now + 0.09);
    const subGain = ctx.createGain();
    subGain.gain.setValueAtTime(0.0, now);
    subGain.gain.linearRampToValueAtTime(0.35, now + 0.006);
    subGain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
    sub.connect(subGain).connect(master);
    sub.start(now);
    sub.stop(now + 0.11);
  } catch {
    // non-critical
  }
}

// ─── Failed transaction sound ────────────────────────────────────────────────

/**
 * Descending buzz — plays on failed/reverted transactions.
 * A short low rumble that descends in pitch, signalling something went wrong
 * without being annoying. Think: a muted "bonk" or error beep.
 * Total: ~250ms.
 */
export function playFailedSound() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.25, now);
    master.connect(ctx.destination);

    // Low descending tone
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(200, now);
    osc.frequency.exponentialRampToValueAtTime(80, now + 0.2);

    const lpf = ctx.createBiquadFilter();
    lpf.type = 'lowpass';
    lpf.frequency.setValueAtTime(400, now);

    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(0.5, now);
    oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

    osc.connect(lpf).connect(oscGain).connect(master);
    osc.start(now);
    osc.stop(now + 0.3);

    // Noise burst for the "impact"
    const noiseLen = Math.round(ctx.sampleRate * 0.01);
    const noiseBuf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
    const noiseData = noiseBuf.getChannelData(0);
    for (let i = 0; i < noiseLen; i++) {
      noiseData[i] = (Math.random() * 2 - 1);
    }
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.3, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.015);
    noise.connect(noiseGain).connect(master);
    noise.start(now);
    noise.stop(now + 0.02);
  } catch {
    // non-critical
  }
}

// ─── Cash-out sound — fee claim ───────────────────────────────────────────────

/**
 * Cash register "cha-ching" — plays on successful fee claim.
 * A quick metallic drawer-slide followed by a bright double-ring.
 * Think: old-fashioned register opening and coins landing.
 * Total: ~400ms.
 */
export function playClaimFeesSound() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.25, now);
    master.connect(ctx.destination);

    // Part 1: drawer slide — filtered noise sweep (0-80ms)
    const slideLen = Math.round(ctx.sampleRate * 0.08);
    const slideBuf = ctx.createBuffer(1, slideLen, ctx.sampleRate);
    const slideData = slideBuf.getChannelData(0);
    for (let i = 0; i < slideLen; i++) {
      slideData[i] = (Math.random() * 2 - 1);
    }
    const slide = ctx.createBufferSource();
    slide.buffer = slideBuf;
    const slideBpf = ctx.createBiquadFilter();
    slideBpf.type = 'bandpass';
    slideBpf.frequency.setValueAtTime(1200, now);
    slideBpf.frequency.exponentialRampToValueAtTime(4000, now + 0.06);
    slideBpf.Q.setValueAtTime(1.0, now);
    const slideGain = ctx.createGain();
    slideGain.gain.setValueAtTime(0.4, now);
    slideGain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
    slide.connect(slideBpf).connect(slideGain).connect(master);
    slide.start(now);
    slide.stop(now + 0.09);

    // Part 2: first ring — bright metallic "ching" at ~100ms
    const ring1 = ctx.createOscillator();
    ring1.type = 'sine';
    ring1.frequency.setValueAtTime(2093, now + 0.08); // C7
    const ring1Gain = ctx.createGain();
    ring1Gain.gain.setValueAtTime(0.001, now);
    ring1Gain.gain.linearRampToValueAtTime(0.6, now + 0.09);
    ring1Gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
    ring1.connect(ring1Gain).connect(master);
    ring1.start(now + 0.08);
    ring1.stop(now + 0.26);

    // Part 3: second ring — higher, softer "ching" at ~180ms
    const ring2 = ctx.createOscillator();
    ring2.type = 'sine';
    ring2.frequency.setValueAtTime(3136, now + 0.16); // G7
    const ring2Gain = ctx.createGain();
    ring2Gain.gain.setValueAtTime(0.001, now);
    ring2Gain.gain.linearRampToValueAtTime(0.35, now + 0.17);
    ring2Gain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);
    ring2.connect(ring2Gain).connect(master);
    ring2.start(now + 0.16);
    ring2.stop(now + 0.4);

    // Shimmer overtone on the second ring
    const shimmer = ctx.createOscillator();
    shimmer.type = 'sine';
    shimmer.frequency.setValueAtTime(3136 * 2.01, now + 0.17);
    const shimGain = ctx.createGain();
    shimGain.gain.setValueAtTime(0.001, now);
    shimGain.gain.linearRampToValueAtTime(0.1, now + 0.18);
    shimGain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    shimmer.connect(shimGain).connect(master);
    shimmer.start(now + 0.17);
    shimmer.stop(now + 0.37);
  } catch {
    // non-critical
  }
}

// ─── Abandon sound — MP3 warp into nothing ──────────────────────────────────

const abandonSoundBuffer = createSoundBufferManager({
  getAudioContext,
  path: "/sounds/abandon.mp3",
});
abandonSoundBuffer.preload();

/**
 * Warp-into-nothing — plays the abandon MP3 sped up to fit the forfeit
 * animation duration (1.2s). The original file is ~2.76s, so playbackRate
 * is set to compress it. Fades out at the tail so it ends cleanly.
 *
 * @param durationMs  Target duration to fit the sound into (default 1200ms).
 */
export function playAbandonSound(durationMs: number = 1200) {
  if (isSoundMuted()) return;
  if (!shouldPlayGridSound('abandon')) return;
  try {
    const ctx = getAudioContext();
    const buf = abandonSoundBuffer.getBuffer();
    if (!buf) {
      void abandonSoundBuffer.loadBuffer();
      return;
    }

    const now = ctx.currentTime;
    const targetSec = durationMs / 1000;
    const rate = buf.duration / targetSec;

    const source = ctx.createBufferSource();
    source.buffer = buf;
    source.playbackRate.setValueAtTime(rate, now);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.35, now);
    // Fade out over the last 20% so it doesn't clip
    gain.gain.setValueAtTime(0.35, now + targetSec * 0.8);
    gain.gain.exponentialRampToValueAtTime(0.001, now + targetSec);

    source.connect(gain).connect(ctx.destination);
    source.start(now);
    source.stop(now + targetSec + 0.05);
  } catch {
    // non-critical
  }
}

// ─── Coin-drop reward sound ─────────────────────────────────────────────────

/**
 * Play a coin-drop sound — a short metallic clink with a quieter bounce.
 */
export function playRewardSound() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.22, now);
    master.connect(ctx.destination);

    // --- Primary hit: metallic clink ---
    coinHit(ctx, master, now, 1.0);

    // --- Bounce: softer, slightly higher pitch, delayed ---
    coinHit(ctx, master, now + 0.09, 0.35);
  } catch {
    // Silently fail — audio is non-critical
  }
}

/**
 * Single metallic coin hit. Layered oscillators for that "clink" timbre.
 */
function coinHit(
  ctx: AudioContext,
  destination: AudioNode,
  startTime: number,
  volume: number,
) {
  const base = 3520; // A7 — bright and metallic
  const duration = 0.08;

  // Layer 1: core tone (sine) — the body of the clink
  const osc1 = ctx.createOscillator();
  const g1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(base, startTime);
  // Pitch drops slightly as the coin settles
  osc1.frequency.exponentialRampToValueAtTime(base * 0.85, startTime + duration);
  g1.gain.setValueAtTime(0.6 * volume, startTime);
  g1.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
  osc1.connect(g1).connect(destination);
  osc1.start(startTime);
  osc1.stop(startTime + duration + 0.01);

  // Layer 2: inharmonic partial (triangle, detuned) — metallic shimmer
  const osc2 = ctx.createOscillator();
  const g2 = ctx.createGain();
  osc2.type = 'triangle';
  osc2.frequency.setValueAtTime(base * 2.76, startTime); // inharmonic ratio for metallic feel
  g2.gain.setValueAtTime(0.25 * volume, startTime);
  g2.gain.exponentialRampToValueAtTime(0.001, startTime + duration * 0.7);
  osc2.connect(g2).connect(destination);
  osc2.start(startTime);
  osc2.stop(startTime + duration + 0.01);

  // Layer 3: noise burst — the "click" transient of impact
  const bufferSize = ctx.sampleRate * 0.015; // 15ms of noise
  const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = noiseBuffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * 0.5;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer;

  // Bandpass filter the noise to keep it metallic, not hissy
  const bpf = ctx.createBiquadFilter();
  bpf.type = 'bandpass';
  bpf.frequency.setValueAtTime(6000, startTime);
  bpf.Q.setValueAtTime(2, startTime);

  const gn = ctx.createGain();
  gn.gain.setValueAtTime(0.4 * volume, startTime);
  gn.gain.exponentialRampToValueAtTime(0.001, startTime + 0.015);

  noise.connect(bpf).connect(gn).connect(destination);
  noise.start(startTime);
  noise.stop(startTime + 0.02);
}

// ─── Graduation sound — celebratory ascending chime ─────────────────────────
// (moved to gong-based version below)

// ─── New coin notification ping ─────────────────────────────────────────────

/**
 * Soft notification ping — plays when a new coin appears in the New bucket.
 * A single bright sine tone (A5) with a gentle attack and quick decay. ~200ms.
 */
export function playNewCoinSound() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.18, now);
    master.connect(ctx.destination);

    // Primary ping — A5 (880 Hz)
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.linearRampToValueAtTime(0.5, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

    osc.connect(gain).connect(master);
    osc.start(now);
    osc.stop(now + 0.25);

    // Subtle octave shimmer
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1760, now);
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.001, now);
    g2.gain.linearRampToValueAtTime(0.12, now + 0.02);
    g2.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
    osc2.connect(g2).connect(master);
    osc2.start(now);
    osc2.stop(now + 0.2);
  } catch {
    // non-critical
  }
}

/**
 * Quick sparkle sound — two ascending sine tones creating a bright twinkle.
 * Plays when a user favorites a grid. ~250ms.
 */
export function playFavoriteSound() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.15, now);
    master.connect(ctx.destination);

    // First sparkle — E6
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(1318.5, now);
    const g1 = ctx.createGain();
    g1.gain.setValueAtTime(0.001, now);
    g1.gain.linearRampToValueAtTime(0.4, now + 0.015);
    g1.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
    osc1.connect(g1).connect(master);
    osc1.start(now);
    osc1.stop(now + 0.15);

    // Second sparkle — A6, slightly delayed
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1760, now + 0.06);
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.001, now + 0.06);
    g2.gain.linearRampToValueAtTime(0.35, now + 0.075);
    g2.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
    osc2.connect(g2).connect(master);
    osc2.start(now + 0.06);
    osc2.stop(now + 0.25);

    // High shimmer — E7
    const osc3 = ctx.createOscillator();
    osc3.type = 'sine';
    osc3.frequency.setValueAtTime(2637, now + 0.1);
    const g3 = ctx.createGain();
    g3.gain.setValueAtTime(0.001, now + 0.115);
    g3.gain.linearRampToValueAtTime(0.15, now + 0.12);
    g3.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
    osc3.connect(g3).connect(master);
    osc3.start(now + 0.1);
    osc3.stop(now + 0.25);
  } catch {
    // non-critical
  }
}

// ─── Graduation chime — short celebratory gong ──────────────────────────────

/**
 * Short graduation chime: bright metallic strike with quick shimmer tail. ~1.2s.
 */
export function playGraduationSound() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.22, now);
    master.connect(ctx.destination);

    // Strike — C5 sine, quick attack
    const strike = ctx.createOscillator();
    strike.type = 'sine';
    strike.frequency.setValueAtTime(523.25, now);
    const sGain = ctx.createGain();
    sGain.gain.setValueAtTime(0.001, now);
    sGain.gain.linearRampToValueAtTime(0.5, now + 0.03);
    sGain.gain.setTargetAtTime(0.001, now + 0.15, 0.25);
    strike.connect(sGain).connect(master);
    strike.start(now);
    strike.stop(now + 1.2);

    // Warm body — G4 triangle
    const body = ctx.createOscillator();
    body.type = 'triangle';
    body.frequency.setValueAtTime(392.0, now);
    const bGain = ctx.createGain();
    bGain.gain.setValueAtTime(0.001, now);
    bGain.gain.linearRampToValueAtTime(0.25, now + 0.04);
    bGain.gain.setTargetAtTime(0.001, now + 0.2, 0.2);
    body.connect(bGain).connect(master);
    body.start(now);
    body.stop(now + 1.0);

    // Shimmer pair — detuned E5
    for (const detune of [-5, 5]) {
      const shimmer = ctx.createOscillator();
      shimmer.type = 'sine';
      shimmer.frequency.setValueAtTime(659.25, now);
      shimmer.detune.setValueAtTime(detune, now);
      const shGain = ctx.createGain();
      shGain.gain.setValueAtTime(0.001, now);
      shGain.gain.linearRampToValueAtTime(0.08, now + 0.06);
      shGain.gain.setTargetAtTime(0.001, now + 0.3, 0.2);
      shimmer.connect(shGain).connect(master);
      shimmer.start(now);
      shimmer.stop(now + 1.0);
    }

    // Metallic overtone
    const ot = ctx.createOscillator();
    ot.type = 'sine';
    ot.frequency.setValueAtTime(523.25 * 2.76, now);
    const otGain = ctx.createGain();
    otGain.gain.setValueAtTime(0.001, now);
    otGain.gain.linearRampToValueAtTime(0.06, now + 0.02);
    otGain.gain.setTargetAtTime(0.001, now + 0.1, 0.15);
    ot.connect(otGain).connect(master);
    ot.start(now);
    ot.stop(now + 0.8);
  } catch {
    // non-critical
  }
}

// ─── Anti-snipe lock sound — dramatic metallic sheen to lock thump ───────────

/**
 * Dramatic game-like metallic sheen into a heavy lock thump. Plays when pokeTax
 * confirms and the anti-snipe window goes live onchain. Three parts:
 *   1. Metallic sheen (detuned sawtooth oscillators with inharmonic ratios)
 *   2. Lock mechanism (noise transient + dual metallic clicks)
 *   3. Heavy lock thump (distorted sine drop + compressed noise impact)
 * Total: ~600ms.
 */
export function playAntiSnipeLockSound() {
  if (isSoundMuted()) return;
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.35, now);
    master.connect(ctx.destination);

    // ── Part 1: metallic sheen (0–250ms) ──
    // Downward-sweeping detuned oscillators at inharmonic ratios — dark, dramatic sword-draw feel
    const sheenBase = [600, 600 * 1.73, 600 * 2.92, 600 * 4.17];
    const sheenAmps = [0.14, 0.10, 0.07, 0.04];
    for (let i = 0; i < sheenBase.length; i++) {
      const startFreq = sheenBase[i] * 3;
      const endFreq = sheenBase[i] * 0.6;
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(startFreq, now);
      osc.frequency.exponentialRampToValueAtTime(endFreq, now + 0.22);
      osc.detune.setValueAtTime((i - 1.5) * 25, now);

      const bpf = ctx.createBiquadFilter();
      bpf.type = 'bandpass';
      bpf.frequency.setValueAtTime(startFreq * 0.8, now);
      bpf.frequency.exponentialRampToValueAtTime(endFreq * 1.5, now + 0.22);
      bpf.Q.setValueAtTime(2.0, now);
      bpf.Q.linearRampToValueAtTime(1.0, now + 0.22);

      const lpf = ctx.createBiquadFilter();
      lpf.type = 'lowpass';
      lpf.frequency.setValueAtTime(4000, now);
      lpf.frequency.exponentialRampToValueAtTime(1500, now + 0.22);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, now);
      g.gain.linearRampToValueAtTime(sheenAmps[i], now + 0.03);
      g.gain.setValueAtTime(sheenAmps[i], now + 0.12);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

      osc.connect(bpf).connect(lpf).connect(g).connect(master);
      osc.start(now);
      osc.stop(now + 0.27);
    }

    // Filtered noise layer — metallic air/texture (mid-range, not bright)
    const noiseLen = Math.round(ctx.sampleRate * 0.2);
    const noiseBuf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
    const noiseData = noiseBuf.getChannelData(0);
    for (let i = 0; i < noiseLen; i++) noiseData[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    const noiseBpf = ctx.createBiquadFilter();
    noiseBpf.type = 'bandpass';
    noiseBpf.frequency.setValueAtTime(2000, now);
    noiseBpf.frequency.exponentialRampToValueAtTime(800, now + 0.2);
    noiseBpf.Q.setValueAtTime(1.5, now);
    const noiseG = ctx.createGain();
    noiseG.gain.setValueAtTime(0.001, now);
    noiseG.gain.linearRampToValueAtTime(0.08, now + 0.03);
    noiseG.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
    noise.connect(noiseBpf).connect(noiseG).connect(master);
    noise.start(now);
    noise.stop(now + 0.22);

    // ── Part 2: lock mechanism (180–350ms) ──
    // Sharp noise transient — bolt snap
    const snapLen = Math.round(ctx.sampleRate * 0.015);
    const snapBuf = ctx.createBuffer(1, snapLen, ctx.sampleRate);
    const snapData = snapBuf.getChannelData(0);
    for (let i = 0; i < snapLen; i++) snapData[i] = Math.random() * 2 - 1;
    const snap = ctx.createBufferSource();
    snap.buffer = snapBuf;
    const snapBpf = ctx.createBiquadFilter();
    snapBpf.type = 'bandpass';
    snapBpf.frequency.setValueAtTime(3200, now + 0.18);
    snapBpf.Q.setValueAtTime(2.5, now + 0.18);
    const snapG = ctx.createGain();
    snapG.gain.setValueAtTime(0.8, now + 0.18);
    snapG.gain.exponentialRampToValueAtTime(0.001, now + 0.22);
    snap.connect(snapBpf).connect(snapG).connect(master);
    snap.start(now + 0.18);
    snap.stop(now + 0.23);

    // Metallic click 1 — square-wave bolt ping dropping in pitch
    const click1 = ctx.createOscillator();
    click1.type = 'square';
    click1.frequency.setValueAtTime(1800, now + 0.19);
    click1.frequency.exponentialRampToValueAtTime(800, now + 0.28);
    const click1Lpf = ctx.createBiquadFilter();
    click1Lpf.type = 'lowpass';
    click1Lpf.frequency.setValueAtTime(2500, now + 0.19);
    const click1G = ctx.createGain();
    click1G.gain.setValueAtTime(0.2, now + 0.19);
    click1G.gain.exponentialRampToValueAtTime(0.001, now + 0.28);
    click1.connect(click1Lpf).connect(click1G).connect(master);
    click1.start(now + 0.19);
    click1.stop(now + 0.3);

    // Metallic click 2 — delayed, higher, shorter (double-click feel)
    const click2 = ctx.createOscillator();
    click2.type = 'square';
    click2.frequency.setValueAtTime(2200, now + 0.24);
    click2.frequency.exponentialRampToValueAtTime(1200, now + 0.3);
    const click2G = ctx.createGain();
    click2G.gain.setValueAtTime(0.12, now + 0.24);
    click2G.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
    click2.connect(click2G).connect(master);
    click2.start(now + 0.24);
    click2.stop(now + 0.32);

    // ── Part 3: heavy lock thump (300–600ms) ──
    // Distorted sine drop via waveshaper for grit/weight
    const thump = ctx.createOscillator();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(90, now + 0.3);
    thump.frequency.exponentialRampToValueAtTime(35, now + 0.55);
    const shaper = ctx.createWaveShaper();
    const curve = new Float32Array(256);
    for (let i = 0; i < 256; i++) {
      const x = (i / 128) - 1;
      curve[i] = (Math.PI + 2) * x / (Math.PI + 2 * Math.abs(x));
    }
    shaper.curve = curve;
    shaper.oversample = '2x';
    const thumpG = ctx.createGain();
    thumpG.gain.setValueAtTime(0.001, now + 0.29);
    thumpG.gain.linearRampToValueAtTime(0.85, now + 0.32);
    thumpG.gain.exponentialRampToValueAtTime(0.001, now + 0.58);
    thump.connect(shaper).connect(thumpG).connect(master);
    thump.start(now + 0.29);
    thump.stop(now + 0.6);

    // Sub-harmonic — floor-shaking finality
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(45, now + 0.3);
    sub.frequency.exponentialRampToValueAtTime(22, now + 0.55);
    const subG = ctx.createGain();
    subG.gain.setValueAtTime(0.001, now + 0.3);
    subG.gain.linearRampToValueAtTime(0.4, now + 0.33);
    subG.gain.exponentialRampToValueAtTime(0.001, now + 0.55);
    sub.connect(subG).connect(master);
    sub.start(now + 0.3);
    sub.stop(now + 0.58);

    // Compressed noise impact — adds percussive body to the thump
    const impactLen = Math.round(ctx.sampleRate * 0.06);
    const impactBuf = ctx.createBuffer(1, impactLen, ctx.sampleRate);
    const impactData = impactBuf.getChannelData(0);
    for (let i = 0; i < impactLen; i++) impactData[i] = Math.random() * 2 - 1;
    const impact = ctx.createBufferSource();
    impact.buffer = impactBuf;
    const impactLpf = ctx.createBiquadFilter();
    impactLpf.type = 'lowpass';
    impactLpf.frequency.setValueAtTime(400, now + 0.3);
    const impactG = ctx.createGain();
    impactG.gain.setValueAtTime(0.3, now + 0.3);
    impactG.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    impact.connect(impactLpf).connect(impactG).connect(master);
    impact.start(now + 0.3);
    impact.stop(now + 0.42);
  } catch {
    // non-critical
  }
}
